import { useEffect, useRef, useState } from 'react'
import {
  Star, MessageSquareQuote, Quote, CalendarRange, CalendarDays, Clock, Users,
  ChevronLeft, ChevronRight, X, Download, FolderDown, Image as ImageIcon,
  Stethoscope, ShieldCheck, AlertCircle, Activity,
  Sparkles, Wind, CircleDot, Minus, Syringe, Shield, ScanLine, Plus,
} from 'lucide-react'

// A small icon per treatment type for the "รักษาแล้ว" chips.
function treatmentIcon(key) {
  return ({
    scaling:    Sparkles,
    airflow:    Wind,
    filling:    CircleDot,
    extraction: Minus,
    rct:        Syringe,
    fluoride:   Shield,
    sealant:    ShieldCheck,
    xray:       ScanLine,
  })[key] || Plus
}
import { fmtDateOnly } from '../lib/dt.js'

/**
 * Shared report-content pieces, rendered identically in the public HR portal
 * (HrReport.jsx) and the staff Operation → Report tab (_OperationReport.jsx):
 *
 *   OperationSummary    — when / how many days / how many patients
 *   CstSatisfactionPanel — แบบประเมินความพึงพอใจ: average + per-question + all comments
 *   ReportPhotoSlider    — a swipeable carousel for the event photos
 *
 * The whole block is intentionally on-brand (amber → orange) rather than the
 * red/amber/green semantic palette, so it matches the Tooth Taxi theme.
 */

// CST question labels — mirror of functions/api/survey/cst/[token].js so the
// staff side can compute the same aggregate without a second round-trip.
export const CST_QUESTIONS = [
  'ขั้นตอนลงทะเบียนก่อนรับการรักษา สะดวกรวดเร็ว ไม่ยุ่งยาก',
  'เจ้าหน้าที่เป็นมิตร และให้คำแนะนำที่ดี',
  'คุณพึงพอใจกับการดูแลรักษาที่ได้รับจากคุณหมอ',
  'รถทันตกรรมสะอาดสะอ้าน รู้สึกมั่นใจในมาตรฐานการฆ่าเชื้อ',
  'โดยรวมแล้วท่านรู้สึกประทับใจในการใช้บริการรถทันตกรรมเคลื่อนที่ Tooth Taxi',
  'ท่านจะแนะนำบริการของ Tooth Taxi ให้กับผู้อื่น หรืออยากใช้บริการอีกครั้งในโอกาสหน้า',
]

// Build the HR-report CST shape ({count, mean, per_question, questions,
// suggestions}) from raw cst_response rows, excluding hidden ones — so the
// staff preview matches exactly what HR sees.
export function aggregateCst(rows) {
  const live = (rows || []).filter((r) => !r.hidden)
  const count = live.length
  if (!count) {
    return { count: 0, mean: null, per_question: [null, null, null, null, null, null], questions: CST_QUESTIONS, suggestions: [] }
  }
  const per_question = [1, 2, 3, 4, 5, 6].map((qi) => {
    const k = `q${qi}`
    return live.reduce((s, r) => s + (r[k] || 0), 0) / count
  })
  const mean = per_question.reduce((s, x) => s + (x || 0), 0) / 6
  const suggestions = live
    .filter((r) => (r.suggestion || '').trim())
    .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
    .map((r) => ({ text: r.suggestion.trim(), at: r.created_at }))
  return { count, mean, per_question, questions: CST_QUESTIONS, suggestions }
}


/* ─────────────────────────── Operation summary ─────────────────────────── */

export function OperationSummary({ o, patients, asOf }) {
  if (!o) return null
  const days = dayCountInclusive(o.start_date, o.end_date)
  const dateRange = (o.end_date && o.end_date !== o.start_date)
    ? `${fmtDateOnly(o.start_date)} – ${fmtDateOnly(o.end_date)}`
    : fmtDateOnly(o.start_date)
  const timeRange = (o.start_time && o.end_time) ? `${o.start_time} – ${o.end_time} น.` : null

  return (
    <div className="mb-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryStat icon={CalendarRange} label="ระยะเวลาให้บริการ" value={dateRange} sub={timeRange} big={false} />
        <SummaryStat icon={CalendarDays}  label="จำนวนวันให้บริการ" value={days.toLocaleString()} suffix="วัน" />
        <SummaryStat icon={Users}         label="ผู้เข้ารับบริการ"   value={Number(patients || 0).toLocaleString()} suffix="คน" />
      </div>
      {asOf ? (
        <p className="mt-1.5 text-[11.5px] text-zinc-400 text-right">จำนวนผู้เข้ารับบริการ — ข้อมูล ณ {fmtSnapshot(asOf)}</p>
      ) : null}
    </div>
  )
}

function SummaryStat({ icon: Icon, label, value, suffix, sub, big = true }) {
  return (
    <div className="rounded-2xl ring-1 ring-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 px-4 py-3.5 flex flex-col items-center text-center">
      <Icon size={18} className="text-amber-600" />
      <div className="mt-1.5 leading-none">
        <span className={(big ? 'text-[26px] md:text-[30px]' : 'text-[16px] md:text-[18px]') + ' font-extrabold tabular-nums text-zinc-900'}>{value}</span>
        {suffix && <span className="ml-1 text-[14px] text-amber-700/80 font-semibold">{suffix}</span>}
      </div>
      {sub && <div className="mt-1 text-[12px] text-zinc-500 tabular-nums inline-flex items-center gap-1"><Clock size={10} /> {sub}</div>}
      <div className="mt-1.5 text-[12.5px] text-zinc-600 font-medium">{label}</div>
    </div>
  )
}


/* ─────────────────────── แบบประเมินความพึงพอใจ ─────────────────────── */

export function CstSatisfactionPanel({ cst, heading = 'แบบประเมินความพึงพอใจ' }) {
  const mean = cst.mean != null ? Number(cst.mean) : null
  return (
    <div>
      <SectionHeading>{heading}</SectionHeading>

      {/* Hero: big average + stars + count (per-question shown once, below) */}
      <div className="rounded-2xl ring-1 ring-amber-200/70 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50/40 p-4 md:p-5 mb-4 text-center">
        <div className="text-[40px] md:text-[48px] font-extrabold tabular-nums leading-none bg-gradient-to-br from-amber-500 to-orange-600 bg-clip-text text-transparent">
          {mean == null ? '—' : mean.toFixed(2)}
        </div>
        <div className="mt-2"><StarRating value={mean || 0} size={20} /></div>
        <div className="mt-1.5 text-[12.5px] text-zinc-600">เต็ม 5.00 · {cst.count.toLocaleString()} ผู้ตอบ</div>
      </div>

      {/* Per-question with full text — borderless rows, divided by hairlines */}
      <div className="mb-5">
        <h4 className="text-[13.5px] md:text-[14px] font-semibold text-zinc-800 mb-1">คะแนนรายข้อ</h4>
        <ul className="divide-y divide-zinc-100">
          {cst.questions.map((q, i) => {
            const v = Number(cst.per_question[i] || 0)
            return (
              <li key={i} className="py-2.5">
                <div className="flex items-center gap-2.5">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center justify-center text-[11.5px] font-bold tabular-nums">{i + 1}</span>
                  <p className="text-[13.5px] md:text-[14px] text-zinc-800 leading-snug flex-1">{q}</p>
                  <span className="shrink-0 text-[15px] md:text-[16px] font-bold tabular-nums text-amber-600">{v.toFixed(2)}</span>
                </div>
                <div className="mt-1.5 ml-[30px] h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500" style={{ width: `${(v / 5) * 100}%` }} />
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      {/* ALL comments */}
      <div>
        <h4 className="text-[14px] md:text-[14.5px] font-semibold text-zinc-900 mb-2.5 inline-flex items-center gap-1.5">
          <MessageSquareQuote size={14} className="text-amber-500" /> ความคิดเห็นจากผู้ใช้บริการ
          <span className="text-[12px] font-normal text-zinc-400">({cst.suggestions.length.toLocaleString()})</span>
        </h4>
        {cst.suggestions.length === 0 ? (
          <p className="text-[14px] text-zinc-400 italic px-1">ยังไม่มีความคิดเห็นเพิ่มเติมจากผู้ใช้บริการ</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {cst.suggestions.map((s, i) => (
              <div key={i} className="relative rounded-xl ring-1 ring-amber-100 bg-gradient-to-br from-amber-50/60 to-orange-50/30 pl-8 pr-3.5 py-3">
                <Quote size={13} className="absolute left-3 top-3.5 text-amber-400/70" />
                <p className="text-[13.5px] md:text-[14px] text-zinc-800 italic leading-relaxed whitespace-pre-wrap">{s.text}</p>
                <p className="mt-1.5 text-[11.5px] text-zinc-400 tabular-nums">{fmtFull(s.at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


/* ─────────────────────── สุขภาพช่องปากโดยรวม ─────────────────────── */

// Anonymized population oral-health summary. `data` is the JSON snapshot from
// operation.oral_health (see functions/api/operations/[id]/oral-health.js).
// Three complementary axes: สภาวะที่ตรวจพบ (findings), การรักษาที่ได้รับ
// (treatments delivered), and ควรติดตามต่อ (follow-up gaps).
export function OralHealthPanel({ data, heading = 'สุขภาพช่องปากโดยรวม' }) {
  if (!data || !data.examined) {
    return (
      <div>
        <SectionHeading>{heading}</SectionHeading>
        <p className="text-[13.5px] text-zinc-400 italic">ยังไม่มีข้อมูลการตรวจช่องปาก</p>
      </div>
    )
  }
  const n = data.examined
  const pct = (x) => (n ? Math.round((Number(x) || 0) / n * 100) : 0)
  const cariesAvg = n ? (Number(data.caries_teeth || 0) / n) : 0
  const healthyPct = pct(data.healthy)
  const needsFollowup = Number(data.needs_followup ?? data.needs_referral ?? 0)
  const calculus = Number(data.calculus_patients ?? data.perio_patients ?? 0)
  const treatments = (Array.isArray(data.treatments) ? data.treatments : []).filter((t) => t.patients > 0)

  // Findings = oral_exam buckets (>0) + an inferred หินปูน entry (patients who
  // were scaled, even if the exam note didn't spell it out). Compact text.
  const findings = (data.by_type || []).filter((t) => t.patients > 0).map((t) => ({ label: t.type, patients: t.patients }))
  if (calculus > 0) findings.push({ label: 'หินปูน', patients: calculus, approx: true })
  findings.sort((a, b) => b.patients - a.patients)

  return (
    <div>
      <SectionHeading>{heading}</SectionHeading>
      <p className="text-[12px] text-zinc-500 -mt-1 mb-4">
        สรุปแบบไม่ระบุตัวตน · จากการตรวจช่องปากผู้เข้ารับบริการ <span className="font-semibold text-zinc-700 tabular-nums">{n.toLocaleString()}</span> คน
      </p>

      {/* Headline — three big-digit cards */}
      <div className="grid grid-cols-3 gap-2.5 md:gap-3 mb-5">
        <BigStat tone="emerald" icon={ShieldCheck} value={healthyPct}          suffix="%"      label="สุขภาพดี"            sub={`${Number(data.healthy || 0).toLocaleString()} คน`} />
        <BigStat tone="amber"   icon={Activity}    value={pct(needsFollowup)}  suffix="%"      label="ควรพบทันตแพทย์ต่อ"     sub={`${needsFollowup.toLocaleString()} คน`} />
        <BigStat tone="rose"    icon={AlertCircle} value={cariesAvg.toFixed(1)} suffix="ซี่/คน" label="ฟันผุเฉลี่ย"          sub={`รวม ${Number(data.caries_teeth || 0).toLocaleString()} ซี่`} />
      </div>

      {/* Findings — a small donut per item, coloured by prevalence */}
      <div className="mb-2">
        <h4 className="text-[12px] font-semibold text-zinc-500 uppercase tracking-wide mb-3">ตรวจพบ</h4>
        {findings.length === 0
          ? <span className="text-[13px] text-zinc-400 italic">ไม่พบความผิดปกติที่บันทึกไว้</span>
          : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-x-2 gap-y-4">
              {findings.map((f) => {
                const p = pct(f.patients)
                return (
                  <div key={f.label} className="flex flex-col items-center text-center gap-1.5" title={`${f.label} ${p}%`}>
                    <MiniRing pct={p} />
                    <div className="text-[12.5px] text-zinc-700 leading-tight">{f.label}{f.approx ? '*' : ''}</div>
                    <div className="text-[11.5px] text-zinc-400 tabular-nums -mt-0.5">{f.patients.toLocaleString()} คน</div>
                  </div>
                )
              })}
            </div>
          )}
      </div>

      {/* Treatments — chips */}
      <Row label="รักษาแล้ว">
        {treatments.length === 0
          ? <span className="text-[13px] text-zinc-400 italic">ไม่มีข้อมูลการรักษา</span>
          : treatments.map((t) => {
              const TIcon = treatmentIcon(t.key)
              return (
                <span key={t.key || t.label} className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full bg-emerald-50 ring-1 ring-emerald-200 text-emerald-800 text-[12.5px]">
                  <TIcon size={12} className="text-emerald-500 shrink-0" />
                  {t.label} <b className="tabular-nums">{t.patients.toLocaleString()}</b>
                </span>
              )
            })}
      </Row>

      {/* Follow-up — one calm line */}
      {needsFollowup > 0 && (
        <div className="mt-3 rounded-xl ring-1 ring-amber-200 bg-amber-50/60 px-3.5 py-2.5 flex items-start gap-2 text-[12.5px] text-zinc-700">
          <Activity size={15} className="text-amber-600 shrink-0 mt-0.5" />
          <span>
            <b className="text-zinc-900">ติดตามต่อ {needsFollowup.toLocaleString()} คน</b>
            {Number(data.gap_caries || 0) > 0 && <> · ฟันผุยังไม่อุด <b className="tabular-nums">{Number(data.gap_caries).toLocaleString()}</b></>}
            {Number(data.gap_referral || 0) > 0 && <> · ถอน/รากที่ยังไม่ทำ <b className="tabular-nums">{Number(data.gap_referral).toLocaleString()}</b></>}
          </span>
        </div>
      )}

      <p className="mt-3 text-[11.5px] text-zinc-400 leading-snug">
        * หินปูน รวมผู้ที่ได้รับการขูดหินปูน (โดยประมาณ)
        {data.at ? ` · ข้อมูล ณ ${fmtSnapshot(data.at)}` : ''}
      </p>
    </div>
  )
}

// A label + free-flowing content row (chips / inline text). Stacks on mobile.
function Row({ label, children }) {
  return (
    <div className="py-2 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
      <span className="shrink-0 w-[72px] text-[12px] font-semibold text-zinc-500 uppercase tracking-wide">{label}</span>
      <div className="flex-1 flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  )
}

// Big-digit stat card, one per headline metric. Tone sets the accent colour.
function BigStat({ tone, icon: Icon, value, suffix, label, sub }) {
  const t = ({
    emerald: { bg: 'from-emerald-50 to-teal-50',  ring: 'ring-emerald-200/70', ic: 'text-emerald-600', num: 'text-emerald-700' },
    amber:   { bg: 'from-amber-50 to-orange-50',  ring: 'ring-amber-200/70',   ic: 'text-amber-600',   num: 'text-amber-700' },
    rose:    { bg: 'from-rose-50 to-pink-50',     ring: 'ring-rose-200/70',    ic: 'text-rose-500',    num: 'text-rose-600' },
  })[tone] || { bg: 'from-zinc-50 to-white', ring: 'ring-zinc-200/70', ic: 'text-zinc-500', num: 'text-zinc-800' }
  return (
    <div className={`rounded-2xl ring-1 ${t.ring} bg-gradient-to-br ${t.bg} px-2.5 py-4 text-center`}>
      <Icon size={18} className={`mx-auto ${t.ic}`} />
      <div className="mt-1.5 leading-none">
        <span className={`text-[28px] md:text-[32px] font-extrabold tabular-nums ${t.num}`}>{value}</span>
        {suffix && <span className={`ml-0.5 text-[12.5px] font-semibold ${t.num} opacity-70`}>{suffix}</span>}
      </div>
      <div className="mt-1.5 text-[12px] md:text-[12.5px] text-zinc-700 font-medium leading-tight">{label}</div>
      {sub && <div className="text-[11.5px] text-zinc-400 tabular-nums mt-0.5">{sub}</div>}
    </div>
  )
}

// Colour for a finding's prevalence — higher % = more concerning (amber→red).
function findingColor(pct) {
  if (pct >= 60) return '#f43f5e'   // rose-500
  if (pct >= 40) return '#f97316'   // orange-500
  if (pct >= 20) return '#f59e0b'   // amber-500
  return '#fbbf24'                  // amber-400
}

// Small donut showing a single percentage, coloured by its value.
function MiniRing({ pct, size = 48, stroke = 5 }) {
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const v = Math.max(0, Math.min(100, Number(pct) || 0))
  const dash = (v / 100) * c
  const mid = size / 2
  const col = findingColor(v)
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={mid} cy={mid} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
        <circle cx={mid} cy={mid} r={r} fill="none" stroke={col} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[11.5px] font-bold tabular-nums" style={{ color: col }}>{v}%</span>
      </div>
    </div>
  )
}

/* ─────────────────────────── Photo slider ─────────────────────────── */

// Swipeable carousel: a large stage with prev/next + counter, a thumbnail
// strip, and a click-to-fullscreen lightbox. Used in the HR portal report.
export function ReportPhotoSlider({ photos, zipHref, heading = 'ภาพประกอบรายงาน' }) {
  const [idx, setIdx] = useState(0)
  const [full, setFull] = useState(false)
  const touch = useRef({ x: 0, dx: 0 })
  const n = Array.isArray(photos) ? photos.length : 0

  useEffect(() => { if (idx > n - 1) setIdx(0) /* eslint-disable-next-line */ }, [n])
  if (!n) return null

  const go = (i) => setIdx(((i % n) + n) % n)
  const prev = () => go(idx - 1)
  const next = () => go(idx + 1)

  function onTouchStart(e) { touch.current = { x: e.touches[0].clientX, dx: 0 } }
  function onTouchMove(e)  { touch.current.dx = e.touches[0].clientX - touch.current.x }
  function onTouchEnd()    { if (touch.current.dx > 40) prev(); else if (touch.current.dx < -40) next() }

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <SectionHeading className="mb-0">{heading}</SectionHeading>
        {zipHref && (
          <a href={zipHref}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13.5px] font-medium shadow-sm shadow-amber-500/20">
            <FolderDown size={13} /> ดาวน์โหลดทั้งหมด (.zip)
          </a>
        )}
      </div>

      {/* Stage */}
      <div className="relative rounded-2xl overflow-hidden ring-1 ring-amber-200/70 shadow-[0_18px_50px_-24px_rgba(217,119,6,0.45)] bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden"
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          <div className="flex h-full transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${idx * 100}%)` }}>
            {photos.map((p) => (
              <button key={p.id} type="button" onClick={() => setFull(true)}
                title="คลิกเพื่อดูภาพเต็ม"
                className="relative w-full h-full shrink-0 flex items-center justify-center bg-zinc-100">
                {p.url
                  ? <img src={p.url} alt={p.filename} className="w-full h-full object-cover" loading="lazy" />
                  : <div className="w-full h-full flex items-center justify-center text-zinc-300"><ImageIcon size={28} /></div>}
              </button>
            ))}
          </div>

          {n > 1 && (
            <>
              <button type="button" onClick={prev} aria-label="ก่อนหน้า"
                className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/85 hover:bg-white text-zinc-700 shadow-md flex items-center justify-center transition">
                <ChevronLeft size={20} />
              </button>
              <button type="button" onClick={next} aria-label="ถัดไป"
                className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/85 hover:bg-white text-zinc-700 shadow-md flex items-center justify-center transition">
                <ChevronRight size={20} />
              </button>
            </>
          )}

          <div className="absolute bottom-2 right-3 px-2 py-0.5 rounded-full bg-zinc-900/55 text-white text-[11.5px] font-medium tabular-nums">
            {idx + 1} / {n}
          </div>

          {/* Dots */}
          {n > 1 && n <= 12 && (
            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {photos.map((_, i) => (
                <button key={i} onClick={() => go(i)} aria-label={`ภาพที่ ${i + 1}`}
                  className={'h-1.5 rounded-full transition-all ' + (i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/55 hover:bg-white/80')} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail strip */}
      {n > 1 && (
        <div className="mt-2.5 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {photos.map((p, i) => (
            <button key={p.id} type="button" onClick={() => go(i)} title={p.filename || 'photo'}
              className={'shrink-0 w-16 h-16 rounded-lg overflow-hidden ring-2 transition ' +
                (i === idx ? 'ring-amber-500 shadow-sm' : 'ring-transparent opacity-60 hover:opacity-100')}>
              {p.url
                ? <img src={p.url} alt={p.filename} className="w-full h-full object-cover" loading="lazy" />
                : <div className="w-full h-full bg-zinc-100 flex items-center justify-center text-zinc-300"><ImageIcon size={16} /></div>}
            </button>
          ))}
        </div>
      )}
      <p className="mt-2 text-[12px] text-zinc-400">{n.toLocaleString()} ภาพ</p>

      {full && (
        <PhotoLightbox photos={photos} index={idx} onClose={() => setFull(false)} onChange={setIdx} />
      )}
    </div>
  )
}

function PhotoLightbox({ photos, index, onClose, onChange }) {
  const p = photos[index]
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape')          onClose()
      else if (e.key === 'ArrowLeft')  onChange((index - 1 + photos.length) % photos.length)
      else if (e.key === 'ArrowRight') onChange((index + 1) % photos.length)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, photos.length])
  if (!p) return null

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-zinc-900/90 backdrop-blur-sm">
      <header className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-zinc-900/60 text-white">
        <span className="text-[13.5px] truncate flex-1 font-medium" title={p.filename}>
          {p.filename || 'photo'} <span className="text-white/50">· {index + 1}/{photos.length}</span>
        </span>
        {p.download_url && (
          <a href={p.download_url} download
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[13px] bg-white/10 hover:bg-white/20 transition">
            <Download size={12} /> ดาวน์โหลด
          </a>
        )}
        <button onClick={onClose} className="w-8 h-8 rounded-md inline-flex items-center justify-center hover:bg-white/15">
          <X size={18} />
        </button>
      </header>
      <div className="flex-1 relative flex items-center justify-center p-3 md:p-6" onClick={onClose}>
        {photos.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); onChange((index - 1 + photos.length) % photos.length) }}
            className="absolute left-2 md:left-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center">
            <ChevronLeft size={22} />
          </button>
        )}
        <img src={p.url} alt={p.filename}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain rounded-md shadow-2xl" />
        {photos.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); onChange((index + 1) % photos.length) }}
            className="absolute right-2 md:right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center">
            <ChevronRight size={22} />
          </button>
        )}
      </div>
    </div>
  )
}


/* ─────────────────────────── shared bits ─────────────────────────── */

function SectionHeading({ children, className = '' }) {
  return (
    <h3 className={'text-[15.5px] md:text-[17px] font-bold text-zinc-900 mb-2 flex items-center gap-2.5 ' + className}>
      <span className="inline-block w-1.5 h-5 md:h-6 rounded-full bg-amber-500" />
      {children}
    </h3>
  )
}

function StarRating({ value, size = 16 }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0))
  const rounded = Math.round(v * 2) / 2
  const full = Math.floor(rounded)
  const hasHalf = rounded - full === 0.5
  return (
    <span className="inline-flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => {
        if (i < full) return <Star key={i} size={size} className="text-amber-400" fill="currentColor" strokeWidth={0} />
        if (i === full && hasHalf) {
          return (
            <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
              <Star size={size} className="text-zinc-200" fill="currentColor" strokeWidth={0} />
              <span className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                <Star size={size} className="text-amber-400" fill="currentColor" strokeWidth={0} />
              </span>
            </span>
          )
        }
        return <Star key={i} size={size} className="text-zinc-200" fill="currentColor" strokeWidth={0} />
      })}
    </span>
  )
}

function dayCountInclusive(start, end) {
  if (!start) return 0
  if (!end || end === start) return 1
  const [sy, sm, sd] = start.split('-').map(Number)
  const [ey, em, ed] = end.split('-').map(Number)
  const a = Date.UTC(sy, sm - 1, sd)
  const b = Date.UTC(ey, em - 1, ed)
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 1
  return Math.round((b - a) / 86400000) + 1
}

function fmtFull(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' +
    d.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}

function fmtSnapshot(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleString('th-TH-u-ca-gregory', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok',
  })
}
